import React, { useRef, useState } from 'react';
import { Worlds, errorMessage } from '../../api/client';
import { World, WorldBackup, backupFileName, isMarkedForRestore } from '../../types';
import { Icon } from '../ui/Icon';
import { Confirm, Empty, Loading, Note, Section } from '../ui/primitives';
import { fmtBytes, fmtDateTime, statusInfo, timeAgo } from '../../utils/format';

interface Props {
  world: World;
  backups: WorldBackup[];
  loading: boolean;
  error: string | null;
  onUploaded: () => void;
  /** Called after a backup is marked / unmarked so the world (its restoreFromBackupId and status) is reloaded. */
  onWorldChanged: () => void;
}

const MAX_BYTES = 100 * 1024 * 1024;
const VALID_EXT = ['.zip', '.tar', '.tar.gz'];

export const backupName = (b: WorldBackup): string => backupFileName(b.key);

type RestoreIntent = { kind: 'mark' | 'unmark'; backup: WorldBackup };

/** Backups list with download, restore mark / unmark, plus upload-and-merge of a player-provided archive. */
const BackupsTab: React.FC<Props> = ({ world, backups, loading, error, onUploaded, onWorldChanged }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadedName, setUploadedName] = useState<string | null>(null);

  const [intent, setIntent] = useState<RestoreIntent | null>(null);
  const [restoreBusy, setRestoreBusy] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const [restoreNotice, setRestoreNotice] = useState<string | null>(null);

  const [snapshotting, setSnapshotting] = useState(false);
  const [snapshotError, setSnapshotError] = useState<string | null>(null);
  const [snapshotName, setSnapshotName] = useState<string | null>(null);

  const st = statusInfo(world.status);
  const running = st.active;
  const restoring = st.key === 'restoring';
  const worldBusy = st.busy;
  const markedName = backupFileName(world.restoreFromBackupId);

  const pick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    setUploadError(null);
    setUploadedName(null);
    if (!f) return;
    if (f.size > MAX_BYTES) {
      setUploadError('File size exceeds the 100 MB limit.');
      return;
    }
    const lower = f.name.toLowerCase();
    if (!VALID_EXT.some((ext) => lower.endsWith(ext))) {
      setUploadError('Only .zip, .tar and .tar.gz files are supported.');
      return;
    }
    setFile(f);
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    setProgress(0);
    setUploadError(null);
    try {
      const res = await Worlds.uploadBackup(world.id, file, setProgress);
      setUploadedName(res.data?.backupFileName || file.name);
      setFile(null);
      onUploaded();
    } catch (e) {
      setUploadError(errorMessage(e, 'Upload failed. Please try again.'));
    } finally {
      setUploading(false);
    }
  };

  const snapshot = async () => {
    if (snapshotting) return;
    setSnapshotting(true);
    setSnapshotError(null);
    setSnapshotName(null);
    try {
      const res = await Worlds.snapshotNow(world.id);
      setSnapshotName(res.backupFileName || backupFileName(res.key));
      onUploaded();
    } catch (e) {
      setSnapshotError(errorMessage(e, 'Failed to take a snapshot. Please try again.'));
    } finally {
      setSnapshotting(false);
    }
  };

  const snapshotButton = (label: string, className: string) => (
    <button
      type="button"
      className={className}
      disabled={snapshotting || !running}
      title={running ? 'Back up the world right now' : 'The world must be running to take a snapshot'}
      onClick={snapshot}
    >
      {snapshotting ? <span className="spin" style={{ borderTopColor: '#fff' }} /> : <Icon name="plus" size={12} />}
      {snapshotting ? 'Taking snapshot…' : label}
    </button>
  );

  const openIntent = (kind: RestoreIntent['kind'], backup: WorldBackup) => {
    setRestoreError(null);
    setRestoreNotice(null);
    setIntent({ kind, backup });
  };

  const runIntent = async () => {
    if (!intent) return;
    const name = backupName(intent.backup);
    setRestoreBusy(true);
    setRestoreError(null);
    try {
      if (intent.kind === 'mark') {
        const res = await Worlds.markRestore(world.id, name);
        setRestoreNotice(
          res.restartingNow
            ? `Restoring ${name}. The world is shutting down and comes back with this backup in a few minutes.`
            : `${name} will be restored the next time the world starts.`
        );
      } else {
        await Worlds.unmarkRestore(world.id, name);
        setRestoreNotice(`${name} is no longer marked for restore. The world starts from its latest backup as usual.`);
      }
      setIntent(null);
      onWorldChanged();
    } catch (e) {
      setRestoreError(errorMessage(e, intent.kind === 'mark' ? 'Failed to mark the backup for restore.' : 'Failed to unmark the backup.'));
    } finally {
      setRestoreBusy(false);
    }
  };

  const sorted = [...backups].sort((a, b) => new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime());
  const markedBackup = sorted.find((b) => isMarkedForRestore(world, b)) || null;

  const confirmCopy = (() => {
    if (!intent) return { title: '', body: '', label: 'Confirm', danger: false };
    const name = backupName(intent.backup);
    if (intent.kind === 'unmark') {
      return {
        title: `Unmark ${name}?`,
        body: 'The backup stays where it is. The world will start from its latest backup as usual.',
        label: 'Unmark',
        danger: false,
      };
    }
    if (running) {
      return {
        title: `Restore ${name} now?`,
        body: 'The world shuts down right now and comes back with this backup, the same as restoring from the in-game backups menu. Everyone in the world is disconnected, and anything built since this backup is lost.',
        label: 'Restore now',
        danger: true,
      };
    }
    return {
      title: `Restore ${name}?`,
      body: 'This backup is marked to be restored. The next time the world starts it replaces the current world data, and anything built since this backup is lost. You can unmark it until then.',
      label: 'Mark for restore',
      danger: true,
    };
  })();

  return (
    <>
      <Section
        title="Backups"
        blurb="A backup is taken every hour while the world is running and every time it shuts down."
        actions={
          <>
            {snapshotButton('Snapshot now', 'btn btn--sm btn--primary')}
            <input ref={fileRef} type="file" accept=".zip,.tar,.tar.gz,application/zip,application/x-tar,application/gzip" style={{ display: 'none' }} onChange={pick} />
            <button type="button" className="btn btn--sm" onClick={() => fileRef.current?.click()} disabled={uploading}>
              <Icon name="upload" size={13} /> Upload archive
            </button>
          </>
        }
      >
        {snapshotError && <Note tone="danger">{snapshotError}</Note>}
        {snapshotName && <Note tone="ok">Snapshot {snapshotName} saved.</Note>}
        {(file || uploading || uploadError || uploadedName) && (
          <div style={{ marginBottom: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {uploadError && <Note tone="danger">{uploadError}</Note>}
            {uploadedName && !file && <Note tone="ok">Uploaded and merged {uploadedName}. It will be used the next time the world starts.</Note>}
            {file && (
              <div className="lrow" style={{ borderBottom: 0, padding: 0 }}>
                <div className="lrow-main">
                  <span className="bkico">
                    <Icon name="upload" size={15} />
                  </span>
                  <span>
                    <b>{file.name}</b>
                    <small>{fmtBytes(file.size)} · merges entities, playerdata, poi and region folders into the latest backup</small>
                  </span>
                </div>
                {!uploading && (
                  <div className="lrow-act" style={{ opacity: 1 }}>
                    <button type="button" className="btn btn--sm btn--primary" onClick={upload}>
                      <Icon name="upload" size={12} /> Upload
                    </button>
                    <button type="button" className="btn btn--sm btn--ghost" onClick={() => setFile(null)}>
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            )}
            {uploading && (
              <div>
                <small style={{ fontSize: 11.5, color: 'var(--ink3)' }}>Uploading… {progress}%</small>
                <div className="prog">
                  <i style={{ width: `${progress}%` }} />
                </div>
              </div>
            )}
          </div>
        )}

        {error && <Note tone="danger">{error}</Note>}
        {restoreNotice && <Note tone="ok">{restoreNotice}</Note>}
        {restoring && markedName && (
          <Note tone="warn" icon="restart">
            Restoring <b>{markedName}</b>. The world comes back with this backup in a few minutes.
          </Note>
        )}
        {!restoring && markedName && (
          <Note tone="warn" icon="restart">
            <span style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ flex: 1, minWidth: 200 }}>
                <b>{markedName}</b> is marked for restore. It replaces the current world the next time it starts.
              </span>
              {markedBackup && (
                <button type="button" className="btn btn--sm btn--ghost" disabled={worldBusy || restoreBusy} onClick={() => openIntent('unmark', markedBackup)}>
                  <Icon name="x" size={12} /> Unmark
                </button>
              )}
            </span>
          </Note>
        )}

        {loading && backups.length === 0 ? (
          <Loading />
        ) : sorted.length === 0 ? (
          <Empty icon="backup" title="No backups yet" action={running ? snapshotButton('Take first snapshot', 'btn btn--primary') : undefined}>
            {running
              ? 'Take a snapshot now, or wait for the hourly backup. One is also taken when the world shuts down.'
              : 'The first backup is taken when the world shuts down, or upload an archive of your own.'}
          </Empty>
        ) : (
          sorted.map((b) => {
            const name = backupName(b);
            const marked = isMarkedForRestore(world, b);
            return (
              <div className="lrow" key={b.key}>
                <div className="lrow-main">
                  <span className="bkico">
                    <Icon name={marked ? 'restart' : 'backup'} size={15} />
                  </span>
                  <span>
                    <b style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      {name}
                      {marked && (
                        <span className="pill pill--restore" style={{ fontSize: 10.5, padding: '2px 8px' }}>
                          {restoring ? 'Restoring now' : 'Restores on next start'}
                        </span>
                      )}
                    </b>
                    <small title={fmtDateTime(b.lastModified)}>
                      {timeAgo(b.lastModified)} · {fmtBytes(b.size)}
                    </small>
                  </span>
                </div>
                <div className="lrow-act">
                  {marked ? (
                    <button type="button" className="btn btn--sm btn--ghost" title="Unmark this backup" disabled={worldBusy || restoreBusy} onClick={() => openIntent('unmark', b)}>
                      <Icon name="x" size={14} /> Unmark
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn--sm btn--ghost"
                      title={running ? 'Restore this backup now (the world restarts)' : 'Restore this backup the next time the world starts'}
                      disabled={worldBusy || restoreBusy}
                      onClick={() => openIntent('mark', b)}
                    >
                      <Icon name="restart" size={14} /> Restore
                    </button>
                  )}
                  <a className="btn btn--sm btn--ghost" href={Worlds.backupDownloadUrl(world.id, name)} download={name} title="Download">
                    <Icon name="download" size={14} /> Download
                  </a>
                </div>
              </div>
            );
          })
        )}
      </Section>

      <Confirm
        open={!!intent}
        title={confirmCopy.title}
        body={confirmCopy.body}
        confirmLabel={confirmCopy.label}
        danger={confirmCopy.danger}
        busy={restoreBusy}
        error={restoreError}
        onCancel={() => !restoreBusy && setIntent(null)}
        onConfirm={runIntent}
      />
    </>
  );
};

export default BackupsTab;
